/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

/**
 *
 * The backend expects to receive an array of rights that has, besides definition properties (like name, label, description), 3 other important ones:
 *
 * 1) rightInstanceUuid -> if null, this marks the right as being just created, ready to be persisted for the first time,
 *                        else it holds a reference for an already existent right instance
 *
 * 2) roleDefinitionUuid -> if null, this marks that the right is a freely assigned right, else it marks the role from which it comes from.
 *                          If this property is not null, roleRightDefinitionUuid also has to be set properly.
 *
 * 3)selected -> this property is linked to the checkboxes on the UI
 *
 *
 * When saving, the backend takes the following decisions for each right:
 *
 * 1) For rightInstanceUuid = null and selected = true, it knows that it has to save a new right instance. If roleDefinitionUuid != null, it will know that it has to assign
 * also a new role to the user
 *
 * 2) For rightInstanceUuid != null and selected = false, it knows that it has to remove a right instance. If roleDefinitionUuid != null, it will know that it has to remove
 * the role from the user.
 *
 * The state is maintained keeping the rights from roles and the additional rights separately, but at submit the lists will be concatenated. The list of additional rights implies
 * further complexity. In order to render them properly, we have to have one entry for each right, no matter if it exists or not. Also, if a right is already assigned from at least one role,
 * the entry from the list must have the readonly property set on true. In some cases, assigning a role deletes an existing additional right entry (because we can only have
 * one entry for each right in the additional right list). However, actual removing an additional right is done by a soft deletion, using the active property.
 * When deleted, the additional right is only hidden until it will become the only right again.
 *
 * On submit, only non-readonly additional rights will be sent to the server. Readonly rights only matter for the UI component.
 *
 *
 * In order for the state of the list of rights to be consistent, it has to obey the following rules:
 * 1) the rights coming from the same role have the same selected value, and they are either all new (rightInstanceUuid==null) or existent (rightInstanceUuid != null)
 * 2) the additional rights list has to have exactly one active entry for each right
 *
 * The methods in this service implement the state changes for the cases of initializing the state, adding and removing a role.
 *
 * **/

import { Injectable } from '@angular/core';
import { RightModel } from '../user/right.model';
import { RightsAndRolesStateModel } from './models/rights-and-roles-state.model';
import { AdditionalRightModel } from './models/additional-right.model';
import { RoleRightDefinitionVo } from '../roles/models/role-right-definition-vo.model';
import { RoleWithRightsVo } from '../roles/models/role-with-rights-vo.model';

@Injectable()
export class RightsAndRolesService {
  removeRole(roleWithRightsVo: RoleWithRightsVo, state: RightsAndRolesStateModel) {
    const roleUuid = roleWithRightsVo.uuid;

    state.roleRights = state.roleRights.filter(
      (right) => !(right.roleDefinitionUuid === roleUuid && !right.rightInstanceUuid)
    );

    state.roleRights.forEach((right) => {
      if (right.roleDefinitionUuid === roleUuid && right.rightInstanceUuid) {
        right.selected = false;
      }
    });

    roleWithRightsVo.rightDefinitions?.forEach((roleRightDefinition: RoleRightDefinitionVo) => {
      state.rightFromRolesCount[roleRightDefinition.rightName] -= 1;

      if (state.rightFromRolesCount[roleRightDefinition.rightName] == 0) {
        state.additionalRights = this.removeReadonlyRightFromAdditionalRights(
          roleRightDefinition.rightName,
          state.additionalRights
        );

        const isInactiveAdditionalRightPresent = this.isNonReadonlyRightNamePresentInAdditionalRights(
          roleRightDefinition.rightName,
          state.additionalRights
        );

        if (isInactiveAdditionalRightPresent) {
          this.activateAdditionalRight(state, roleRightDefinition.rightName);
        } else {
          const additionalUnselectedRight = this.mapRightFromRoleRightDefinitionVO(
            roleRightDefinition,
            null,
            null,
            false
          );
          state.additionalRights.push(new AdditionalRightModel(additionalUnselectedRight, false));
        }
      }
    });

    state.additionalRights = this.sortAdditionalRights(state.additionalRights);

    return state;
  }

  addRole(roleWithRightsVO: RoleWithRightsVo, state: RightsAndRolesStateModel) {
    if (this.isRolePresentInRights(roleWithRightsVO.uuid, state.roleRights)) {
      state.roleRights.forEach((right) => {
        if (right.roleDefinitionUuid === roleWithRightsVO.uuid) {
          right.selected = true;
        }
      });
    } else {
      const roleUuid = roleWithRightsVO.uuid;
      roleWithRightsVO.rightDefinitions?.forEach((roleRightDefinitionVO: RoleRightDefinitionVo) => {
        state.roleRights.push(
          this.mapRightFromRoleRightDefinitionVO(roleRightDefinitionVO, roleUuid, roleRightDefinitionVO.uuid, true)
        );
      });
    }

    roleWithRightsVO.rightDefinitions?.forEach((roleRightDefinitionVO: RoleRightDefinitionVo) => {
      state.rightFromRolesCount[roleRightDefinitionVO.rightName] += 1;
      if (state.rightFromRolesCount[roleRightDefinitionVO.rightName] == 1) {
        //hide additional right and add new readonly right
        state.additionalRights = this.hideAdditionalRightWithRightName(
          roleRightDefinitionVO.rightName,
          state.additionalRights
        );
        state.additionalRights.push(
          new AdditionalRightModel(
            this.mapRightFromRoleRightDefinitionVO(roleRightDefinitionVO, null, null, true),
            true
          )
        );
      }
    });

    state.additionalRights = this.sortAdditionalRights(state.additionalRights);

    return state;
  }

  getStateFromInitialRights(rights: RightModel[]) {
    const state: RightsAndRolesStateModel = {
      roleRights: [],
      additionalRights: [],
      rightFromRolesCount: {},
    };

    const additionalRightNamesExistenceMap = {};

    state.roleRights = rights.filter((right) => right.roleDefinitionUuid && right.roleDefinitionUuid.length > 0);

    state.additionalRights = rights
      .filter((right) => !right.roleDefinitionUuid)
      .map((right) => new AdditionalRightModel(right, false));

    state.roleRights.forEach((right) => {
      if (this.isNonReadonlyRightNamePresentInAdditionalRights(right.rightName, state.additionalRights)) {
        state.additionalRights = this.hideAdditionalRightWithRightName(right.rightName, state.additionalRights);
      }
    });

    state.additionalRights.forEach((additionalRight) => {
      if (additionalRight.active) {
        additionalRightNamesExistenceMap[additionalRight.right.rightName] = true;
        state.rightFromRolesCount[additionalRight.right.rightName] = 0;
      }
    });

    state.roleRights.forEach((right) => {
      if (!state.rightFromRolesCount[right.rightName]) {
        state.rightFromRolesCount[right.rightName] = 0;
      }

      state.rightFromRolesCount[right.rightName] += 1;

      if (!additionalRightNamesExistenceMap[right.rightName]) {
        state.additionalRights.push(new AdditionalRightModel(right, true));
        additionalRightNamesExistenceMap[right.rightName] = true;
      }
    });

    state.additionalRights = this.sortAdditionalRights(state.additionalRights);

    return state;
  }

  getFinalRightsFromState(state: RightsAndRolesStateModel) {
    const additionalRights = state.additionalRights
      .filter((additionalRight) => !additionalRight.readonly)
      .map((additionalRight) => additionalRight.right);

    return [...state.roleRights, ...additionalRights];
  }

  markAllAdditionalRightsWithSelected(state: RightsAndRolesStateModel, selected: boolean) {
    state.additionalRights.forEach((additionalRight) => {
      if (additionalRight.active && !additionalRight.readonly) {
        additionalRight.right.selected = selected;
      }
    });

    return state;
  }

  private activateAdditionalRight(state: RightsAndRolesStateModel, rightName: string) {
    for (const additionalRight of state.additionalRights) {
      if (additionalRight.right?.rightName === rightName && !additionalRight.active && !additionalRight.readonly) {
        additionalRight.active = true;
      }
    }
  }

  private isRolePresentInRights(roleUuid: string, rights: RightModel[]): boolean {
    for (const right of rights) {
      if (right.roleDefinitionUuid === roleUuid) {
        return true;
      }
    }

    return false;
  }

  private isNonReadonlyRightNamePresentInAdditionalRights(rightName: string, additionalRights: AdditionalRightModel[]) {
    for (const additionalRight of additionalRights) {
      if (!additionalRight.readonly && additionalRight.right?.rightName === rightName) {
        return true;
      }
    }
    return false;
  }

  private mapRightFromRoleRightDefinitionVO(
    roleRightDefinitionVO: RoleRightDefinitionVo,
    roleUuid: string,
    roleRightDefinitionUuid: string,
    selected: boolean
  ): RightModel {
    return {
      rightInstanceUuid: null,
      rightName: roleRightDefinitionVO.rightName,
      rightLabel: roleRightDefinitionVO.rightLabel,
      description: roleRightDefinitionVO.description,
      roleDefinitionUuid: roleUuid,
      roleRightDefinitionUuid: roleRightDefinitionUuid,
      selected: selected,
    };
  }

  private removeReadonlyRightFromAdditionalRights(rightName: string, additionalRights: AdditionalRightModel[]) {
    return additionalRights.filter(
      (additionalRight) => !(additionalRight.readonly && additionalRight.right?.rightName === rightName)
    );
  }

  private hideAdditionalRightWithRightName(rightName: string, additionalRights: AdditionalRightModel[]) {
    additionalRights.forEach((additionalRight) => {
      if (!additionalRight.readonly && additionalRight.right?.rightName === rightName) {
        additionalRight.active = false;
      }
    });

    return additionalRights;
  }

  private sortAdditionalRights(additionalRights: AdditionalRightModel[]) {
    return additionalRights.sort((right1, right2) => right1.right?.rightName?.localeCompare(right2.right?.rightName));
  }
}
